import { useState, useEffect, useRef } from 'react';
import { fetchAppVersions } from '../utils/helpers';
import { APP_VERSION, getAppPlatform, compareVersions } from '../utils/version';

const POLL_INTERVAL_MS = 10 * 60 * 1000; // re-check every 10 minutes
const SOFT_DISMISSED_KEY = 'ml_soft_update_dismissed_v'; // + version suffix

/**
 * useForceUpdate
 *
 * On every mount (and then every POLL_INTERVAL_MS) calls GET /users/appversions.
 * Returns:
 *   status:        'idle' | 'checking' | 'up-to-date' | 'soft-update' | 'force-update'
 *   latestVersion: string
 *   updateMessage: string | null
 *   downloadUrl:   string | null
 *   dismiss():     dismiss a soft-update banner (persisted in localStorage)
 */
export function useForceUpdate() {
  const [state, setState] = useState({
    status: 'idle',
    latestVersion: APP_VERSION,
    updateMessage: null,
    downloadUrl: null,
  });
  const intervalRef = useRef(null);

  const check = async () => {
    setState((s) => ({ ...s, status: 'checking' }));
    try {
      const { data } = await fetchAppVersions();
      const platform = getAppPlatform();

      // DB returns null for a platform if no row exists — that means no update info, stay idle
      const entry = data?.[platform] ?? data?.android ?? null;

      if (!entry || !entry.version) {
        setState((s) => ({ ...s, status: 'up-to-date' }));
        return;
      }

      const { version: latest, forceUpdate, updateMessage, downloadUrl } = entry;
      const cmp = compareVersions(APP_VERSION, latest);

      if (cmp >= 0) {
        // Current version is equal or newer
        setState({ status: 'up-to-date', latestVersion: latest, updateMessage, downloadUrl });
        return;
      }

      // Current < latest — decide force vs soft
      if (forceUpdate) {
        setState({ status: 'force-update', latestVersion: latest, updateMessage, downloadUrl });
      } else {
        // Check if user already dismissed this soft update
        const dismissed = localStorage.getItem(SOFT_DISMISSED_KEY + latest) === '1';
        setState({
          status: dismissed ? 'up-to-date' : 'soft-update',
          latestVersion: latest,
          updateMessage,
          downloadUrl,
        });
      }
    } catch {
      // Network error or API down — don't block the app
      setState((s) => ({ ...s, status: 'up-to-date' }));
    }
  };

  useEffect(() => {
    check();
    intervalRef.current = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    if (state.status === 'soft-update') {
      localStorage.setItem(SOFT_DISMISSED_KEY + state.latestVersion, '1');
      setState((s) => ({ ...s, status: 'up-to-date' }));
    }
  };

  return { ...state, dismiss };
}
